import { useFormik } from "formik";
import React, { useState } from "react";
import { FaXmark } from "react-icons/fa6";
import { ActionIcon, Button, Modal, Password, Title } from "rizzui";
import * as yup from "yup";
import useHandleErrors from "../../../../../hooks/useHandleErrors";
import { UserService } from "../../../../../services/user/userService";
import { toast } from "sonner";

interface PasswordChangeModalProps {
	userId: string;
	open: boolean;
	onClose: () => void;
}

const passwordSchema = yup.object({
	currentPassword: yup.string().required("La contraseña actual es obligatoria"),
	password: yup.string().min(8, 'Contraseña de al menos 8 caracteres').max(30, 'Contraseña menor de 30 caracteres').matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/, 'La contraseña debe contener al menos una letra mayúscula, una minúscula y un número')
		.when('isCreateMode', {
			is: (val: boolean) => val == true,
			then: (schema: yup.Schema) => schema.required('Campo obligatorio'),
			otherwise: (schema: yup.Schema) => schema.nullable()
		})
		.required("La nueva contraseña es obligatoria"),
	passwordConfirm: yup.string()
		.when('password', {
			is: (val: string | null | undefined) => val !== null && val !== undefined && val.length > 0,
			then: (schema: yup.Schema) => schema.oneOf([yup.ref('password'), ''], 'Las contraseñas no coinciden'),
			otherwise: (schema: yup.Schema) => schema.nullable(),
		})
		.when('isCreateMode', {
			is: (val: boolean) => val == true,
			then: (schema: yup.Schema) => schema.required('Campo obligatorio'),
			otherwise: (schema: yup.Schema) => schema.nullable()
		})
		.required("La confirmación de contraseña es obligatoria"),
});

const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({ userId, open, onClose }) => {

	// STATES
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { handleErrors } = useHandleErrors();

	// Formik with fields
	const formik = useFormik({
		initialValues: {
			currentPassword: '',
			password: '',
			passwordConfirm: '',
		},
		validationSchema: passwordSchema,
		validateOnBlur: false,
		onSubmit: async (values, { setSubmitting, resetForm }) => {
			try {
				const response = (await (new UserService()).changePasswordWithConfirmation(userId, values.currentPassword, values.password)).getResponseData();

				if (response && response.success){
					toast.success('Contraseña cambiada con éxito');
					resetForm();
					onClose();
				} else {
					toast.error(response?.message || 'Error al cambiar la contraseña');
				}
			} catch (error) {
				handleErrors(error);
			} finally {
				setSubmitting(false);
			}
		},
	});

	// RENDER
	return (
		<Modal isOpen={open} onClose={onClose} size="sm">
			<div className="m-auto px-7 pt-6 pb-8">
				<div className="mb-7 flex items-center justify-between">
					<Title as="h3">Cambiar Contraseña</Title>
					<ActionIcon
						size="sm"
						variant="text"
						onClick={() => onClose()}
					>
						<FaXmark className="h-auto w-6" strokeWidth={1.8} />
					</ActionIcon>
				</div>

				<div>
					<div className="flex flex-col gap-4">
						<Password
							id='currentPassword'
							label="Contraseña actual"
							className="[&>label>span]:font-medium md:col-span-12"
							inputClassName="text-sm"
							onChange={formik.handleChange}
							value={formik.values.currentPassword}
							error={formik.errors.currentPassword}
						/>
						<Password
							id='password'
							label="Contraseña"
							className="[&>label>span]:font-medium md:col-span-12"
							inputClassName="text-sm"
							onChange={formik.handleChange}
							value={formik.values.password}
							error={formik.errors.password}
						/>
						<Password
							id='passwordConfirm'
							label="Confirmar contraseña"
							className="[&>label>span]:font-medium md:col-span-12"
							inputClassName="text-sm"
							onChange={formik.handleChange}
							value={formik.values.passwordConfirm}
							error={formik.errors.passwordConfirm}
						/>
					</div>
					<div className="flex justify-end gap-2 mt-6">
						<Button type="button" variant="outline" color="secondary" onClick={onClose} disabled={isSubmitting}>
							Cancelar
						</Button>
						<Button type="submit" color="primary" isLoading={isSubmitting} disabled={isSubmitting} onClick={() => formik.handleSubmit()}>
							Cambiar contraseña
						</Button>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default PasswordChangeModal;